import React, { useEffect, useState, useMemo } from 'react';
import api from '../api/api';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faSyncAlt,
    faSearch,
    faTimes,
    faPen,
    faTrash,
    faCalendarAlt,
    faBullseye,
    faUserPlus,
    faBuilding,
    faCoins,
    faTable,
    faThLarge,
    faSortAmountDown,
    faSortAmountUp,
    faTrophy,
    faCheckCircle,
    faExclamationTriangle,
    faClock
} from '@fortawesome/free-solid-svg-icons';

// Helper to compute default half-year dates based on current month
const getDefaultHalfYearDates = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    return month >= 6
        ? { start: `${year}-07-01`, end: `${year}-12-31`, label: `H1 FY${String(year + 1).slice(2)}` }
        : { start: `${year}-01-01`, end: `${year}-06-30`, label: `H2 FY${String(year).slice(2)}` };
};

// Progress bar color classification
const getProgressColorClass = (pct) => {
    if (pct >= 100) return 'fill-green';
    if (pct >= 70) return 'fill-blue';
    if (pct >= 40) return 'fill-amber';
    return 'fill-red';
};

function Targets() {
    const { empId, roleId, isSuper, isManager, canDelete, getRbacParams, activeOrgId, canWrite } = useAuth();
    const isAdmin = isSuper || isManager;
    const currentEmpId = empId;

    const defaultDates = useMemo(() => getDefaultHalfYearDates(), []);

    // Filter & Search States
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('ALL'); // ALL, Amount, Lead Generation, Lead Approaching
    const [statusFilter, setStatusFilter] = useState('ALL'); // ALL, In Progress, Achieved, Exceeded, Needs Attention
    const [periodFilter, setPeriodFilter] = useState('ALL');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    // View & Sorting States
    const [viewMode, setViewMode] = useState('table'); // 'table' | 'cards'
    const [sortField, setSortField] = useState('TargetID');
    const [sortOrder, setSortOrder] = useState('desc');
    const [entries, setEntries] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Data States
    const [targets, setTargets] = useState([]);
    const [summary, setSummary] = useState(null);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [modalUserSearch, setModalUserSearch] = useState('');
    const [form, setForm] = useState({
        TargetID: '',
        AssignedTo: '',
        Period: defaultDates.label,
        TargetType: 'Amount', // Amount | Lead Generation | Lead Approaching
        TargetAmount: '2500000',
        TargetCount: 25,
        StartDate: defaultDates.start,
        EndDate: defaultDates.end,
        Status: 'In Progress',
        Notes: ''
    });

    // Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: null
    });

    const fetchData = async () => {
        try {
            setLoading(true);
            const rbac = getRbacParams();
            const qstr = new URLSearchParams(rbac).toString();
            const [targetsRes, summaryRes, usersRes] = await Promise.all([
                api.get(`/targets?${qstr}`),
                api.get(`/targets/summary?${qstr}`),
                api.get(`/users?${qstr}`)
            ]);

            setTargets(targetsRes.data || []);
            if (summaryRes.data) {
                setSummary(summaryRes.data);
            }
            setUsers(usersRes.data || []);
        } catch (err) {
            console.error("Failed to load targets:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [roleId, empId, activeOrgId]);

    // Team members available: backend tree query returns allowed users
    const assignableTeamMembers = useMemo(() => {
        if (users && users.length > 0) {
            return users.filter(u => !roleId || Number(u.RoleID) <= Number(roleId));
        }
        return [];
    }, [users, roleId]);

    const filteredAssignableUsers = useMemo(() => {
        if (!modalUserSearch.trim()) return assignableTeamMembers;
        const term = modalUserSearch.toLowerCase();
        return assignableTeamMembers.filter(u =>
            u.name?.toLowerCase().includes(term) ||
            u.email?.toLowerCase().includes(term) ||
            u.designation?.toLowerCase().includes(term)
        );
    }, [assignableTeamMembers, modalUserSearch]);

    // Distinct periods list for filter dropdown
    const uniquePeriods = useMemo(() => {
        const set = new Set();
        targets.forEach(t => {
            if (t.Period) set.add(t.Period);
        });
        return Array.from(set).sort();
    }, [targets]);

    // Open Modal for Assignment
    const openCreate = (defaultType = 'Amount') => {
        setEditMode(false);
        setModalUserSearch('');
        const defaultAssignee = assignableTeamMembers.find(u => Number(u.ReportingTo) === Number(empId))?.empId ||
            (assignableTeamMembers.length > 0 ? assignableTeamMembers[0].empId : (empId || ''));

        setForm({
            TargetID: '',
            AssignedTo: defaultAssignee,
            Period: defaultDates.label,
            TargetType: defaultType,
            TargetAmount: defaultType === 'Amount' ? '2500000' : '0',
            TargetCount: defaultType === 'Lead Generation' ? 25 : defaultType === 'Lead Approaching' ? 40 : 10,
            StartDate: startDate || defaultDates.start,
            EndDate: endDate || defaultDates.end,
            Status: 'In Progress',
            Notes: ''
        });
        setShowModal(true);
    };

    const handleEdit = (target) => {
        setEditMode(true);
        setModalUserSearch('');
        setForm({
            TargetID: target.TargetID,
            AssignedTo: target.AssignedTo,
            Period: target.Period || defaultDates.label,
            TargetType: target.TargetType || 'Amount',
            TargetAmount: target.TargetAmount || '',
            TargetCount: target.TargetCount || 10,
            StartDate: target.StartDate ? target.StartDate.slice(0, 10) : defaultDates.start,
            EndDate: target.EndDate ? target.EndDate.slice(0, 10) : defaultDates.end,
            Status: target.Status || 'In Progress',
            Notes: target.Notes || ''
        });
        setShowModal(true);
    };

    const handleDelete = (id) => {
        setConfirmModal({
            open: true,
            title: 'Confirm Delete Target Allocation',
            message: 'Are you sure you want to remove this target allocation from the quota tracking ledger?',
            onConfirm: async () => {
                try {
                    await api.delete(`/targets/${id}`);
                    fetchData();
                } catch (err) {
                    console.error("Delete target failed:", err);
                    alert("Failed to delete target");
                }
            }
        });
    };

    const handleSave = (e) => {
        if (e) e.preventDefault();
        if (!form.AssignedTo) {
            alert("Please select a team member");
            return;
        }

        const assignedUser = users.find(u => Number(u.empId) === Number(form.AssignedTo));
        const repName = assignedUser ? assignedUser.name : 'Team Member';
        const isAmount = form.TargetType === 'Amount';
        const targetVal = isAmount ? `$${Number(form.TargetAmount).toLocaleString('en-US')}` : `${form.TargetCount} Leads`;

        setConfirmModal({
            open: true,
            title: editMode ? 'Confirm Target Quota Update' : 'Confirm Assign Target Quota',
            message: editMode
                ? `Are you sure you want to update ${repName}'s ${form.TargetType} target to ${targetVal} for ${form.Period}?`
                : `Are you sure you want to assign a ${form.TargetType} target of ${targetVal} to ${repName} for ${form.Period}?`,
            onConfirm: async () => {
                try {
                    if (editMode) {
                        await api.put(`/targets/${form.TargetID}`, form);
                    } else {
                        await api.post('/targets', { ...form, AssignedBy: currentEmpId });
                    }
                    setShowModal(false);
                    fetchData();
                } catch (err) {
                    console.error("Save target failed:", err);
                    alert("Failed to save target");
                }
            }
        });
    };

    // Filter targets by search term, Type, Status, Period, and Dates
    const filteredTargets = useMemo(() => {
        return targets.filter(t => {
            // Search filter
            if (search.trim()) {
                const term = search.toLowerCase();
                const nameMatch = t.EmployeeName?.toLowerCase().includes(term);
                const desigMatch = t.EmployeeDesignation?.toLowerCase().includes(term);
                const periodMatch = t.Period?.toLowerCase().includes(term);
                const notesMatch = t.Notes?.toLowerCase().includes(term);
                const idMatch = String(t.TargetID).includes(term);
                if (!nameMatch && !desigMatch && !periodMatch && !notesMatch && !idMatch) {
                    return false;
                }
            }

            // Type filter
            if (typeFilter !== 'ALL' && t.TargetType !== typeFilter) {
                return false;
            }

            // Status filter
            if (statusFilter !== 'ALL' && t.Status !== statusFilter) {
                return false;
            }

            // Period filter
            if (periodFilter !== 'ALL' && t.Period !== periodFilter) {
                return false;
            }

            // Date filter
            if (t.StartDate && startDate) {
                const targetStart = t.StartDate.slice(0, 10);
                if (targetStart < startDate) return false;
            }
            if (t.EndDate && endDate) {
                const targetEnd = t.EndDate.slice(0, 10);
                if (targetEnd > endDate) return false;
            }

            return true;
        });
    }, [targets, search, typeFilter, statusFilter, periodFilter, startDate, endDate]);

    // Sorting
    const sortedTargets = useMemo(() => {
        const list = [...filteredTargets];
        list.sort((a, b) => {
            let valA = a[sortField];
            let valB = b[sortField];

            if (sortField === 'progress') {
                valA = Number(a.ProgressPercent) || 0;
                valB = Number(b.ProgressPercent) || 0;
            } else if (sortField === 'target') {
                valA = a.TargetType === 'Amount' ? (Number(a.TargetAmount) || 0) : (Number(a.TargetCount) || 0);
                valB = b.TargetType === 'Amount' ? (Number(b.TargetAmount) || 0) : (Number(b.TargetCount) || 0);
            } else if (sortField === 'achieved') {
                valA = Number(a.AchievedDisplay) || 0;
                valB = Number(b.AchievedDisplay) || 0;
            } else if (sortField === 'EmployeeName') {
                valA = (a.EmployeeName || '').toLowerCase();
                valB = (b.EmployeeName || '').toLowerCase();
            }

            if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
            if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
        return list;
    }, [filteredTargets, sortField, sortOrder]);

    // Pagination
    const totalPages = Math.ceil(sortedTargets.length / entries);
    const paginatedTargets = useMemo(() => {
        const start = (currentPage - 1) * entries;
        return sortedTargets.slice(start, start + entries);
    }, [sortedTargets, currentPage, entries]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('desc');
        }
    };

    // Calculate 4 KPI Metrics spanning full width
    const kpiMetrics = useMemo(() => {
        // Amount targets
        const amtTargets = filteredTargets.filter(t => t.TargetType === 'Amount');
        const compTarget = amtTargets.reduce((sum, t) => sum + (Number(t.TargetAmount) || 0), 0);
        const wonRev = amtTargets.reduce((sum, t) => sum + (Number(t.AchievedAmount) || 0), 0);
        const revProgress = compTarget > 0 ? Math.min(100, Math.round((wonRev / compTarget) * 100)) : (wonRev > 0 ? 100 : 0);

        // Lead generation targets
        const genTargets = filteredTargets.filter(t => t.TargetType === 'Lead Generation');
        const genTarget = genTargets.reduce((sum, t) => sum + (Number(t.TargetCount) || 0), 0);
        const genAchieved = genTargets.reduce((sum, t) => sum + (Number(t.AchievedLeadsGenerated) || 0), 0);
        const genProgress = genTarget > 0 ? Math.min(100, Math.round((genAchieved / genTarget) * 100)) : 0;

        // Lead approaching targets
        const approachTargets = filteredTargets.filter(t => t.TargetType === 'Lead Approaching');
        const approachTarget = approachTargets.reduce((sum, t) => sum + (Number(t.TargetCount) || 0), 0);
        const approachAchieved = approachTargets.reduce((sum, t) => sum + (Number(t.AchievedLeadsApproached) || 0), 0);
        const approachProgress = approachTarget > 0 ? Math.min(100, Math.round((approachAchieved / approachTarget) * 100)) : 0;

        // Attainment
        const achievedCount = filteredTargets.filter(t => (Number(t.ProgressPercent) || 0) >= 100).length;
        const quotaAttainment = filteredTargets.length > 0 ? Math.round((achievedCount / filteredTargets.length) * 100) : 0;

        return {
            compTarget,
            wonRev,
            revProgress,
            genTarget,
            genAchieved,
            genProgress,
            approachTarget,
            approachAchieved,
            approachProgress,
            achievedCount,
            quotaAttainment,
            totalAllocations: filteredTargets.length
        };
    }, [filteredTargets]);

    const isFiltered = Boolean(
        search.trim() ||
        typeFilter !== 'ALL' ||
        statusFilter !== 'ALL' ||
        periodFilter !== 'ALL' ||
        startDate ||
        endDate
    );

    const handleResetFilters = () => {
        setSearch('');
        setTypeFilter('ALL');
        setStatusFilter('ALL');
        setPeriodFilter('ALL');
        setStartDate('');
        setEndDate('');
        setCurrentPage(1);
    };

    return (
        <Layout>
            <div className="dome-dashboard-wrap">

                {/* ================= 1. TOP HEADER ================= */}
                <div className="dome-dash-header-row">
                    <div className="dome-dash-title-wrap">
                        <h1 className="dome-dash-h1">Targets & Quotas</h1>
                    </div>

                    <div className="dome-dash-actions">
                        <button
                            type="button"
                            className="btn-dome-secondary"
                            title="Refresh targets data"
                            onClick={fetchData}
                            disabled={loading}
                        >
                            <FontAwesomeIcon icon={faSyncAlt} spin={loading} />
                            <span>Refresh</span>
                        </button>

                        {isAdmin && (
                            <button
                                type="button"
                                className={`btn-dome-new ${canWrite ? '' : 'btn-action-disabled'}`}
                                onClick={() => openCreate('Amount')}
                                disabled={!canWrite}
                                title={!canWrite ? 'Select a specific organization to assign targets (disabled in All Organizations mode)' : 'Assign New Target'}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                <span>Assign Target</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* ================= 2. 4-CARD KPI RIBBON ================= */}
                <div className="dome-kpi-strip">
                    {/* Card 1: Revenue Quota */}
                    <div
                        className={`dome-kpi-card cursor-pointer ${typeFilter === 'Amount' ? 'active-filter-card' : ''}`}
                        onClick={() => {
                            setTypeFilter(prev => prev === 'Amount' ? 'ALL' : 'Amount');
                            setCurrentPage(1);
                        }}
                        title="Filter Revenue Quotas"
                    >
                        <span className="dome-kpi-label">Revenue Quota ($)</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">${kpiMetrics.compTarget.toLocaleString("en-US")}</span>
                        </div>
                        <span className="dome-kpi-sub">
                            ${kpiMetrics.wonRev.toLocaleString("en-US")} won ({kpiMetrics.revProgress}% attained)
                        </span>
                    </div>

                    {/* Card 2: Lead Generation */}
                    <div
                        className={`dome-kpi-card cursor-pointer ${typeFilter === 'Lead Generation' ? 'active-filter-card' : ''}`}
                        onClick={() => {
                            setTypeFilter(prev => prev === 'Lead Generation' ? 'ALL' : 'Lead Generation');
                            setCurrentPage(1);
                        }}
                        title="Filter Lead Gen Quotas"
                    >
                        <span className="dome-kpi-label">Lead Gen Quota</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num text-success">{kpiMetrics.genTarget} Leads</span>
                        </div>
                        <span className="dome-kpi-sub">
                            {kpiMetrics.genAchieved} acquired ({kpiMetrics.genProgress}% completed)
                        </span>
                    </div>

                    {/* Card 3: Lead Outreach */}
                    <div
                        className={`dome-kpi-card cursor-pointer ${typeFilter === 'Lead Approaching' ? 'active-filter-card' : ''}`}
                        onClick={() => {
                            setTypeFilter(prev => prev === 'Lead Approaching' ? 'ALL' : 'Lead Approaching');
                            setCurrentPage(1);
                        }}
                        title="Filter Lead Outreach Quotas"
                    >
                        <span className="dome-kpi-label">Outreach Quota</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num text-primary">{kpiMetrics.approachTarget} Approached</span>
                        </div>
                        <span className="dome-kpi-sub">
                            {kpiMetrics.approachAchieved} contacted ({kpiMetrics.approachProgress}% completed)
                        </span>
                    </div>

                    {/* Card 4: Quota Completion */}
                    <div className="dome-kpi-card">
                        <span className="dome-kpi-label">Quota Attainment</span>
                        <div className="dome-kpi-val-row">
                            <span className="dome-kpi-num">{kpiMetrics.achievedCount} / {kpiMetrics.totalAllocations}</span>
                        </div>
                        <span className="dome-kpi-sub">
                            {kpiMetrics.quotaAttainment}% quotas achieved or exceeded
                        </span>
                    </div>
                </div>

                {/* ================= 3. FILTER TOOLBAR (2-ROW SYSTEM) ================= */}
                <div className="dome-dash-subnav-strip dome-opp-subnav">
                    {/* Row 1: Full-Width Search Box */}
                    <div className="dome-opp-search-row">
                        <div className="dome-search-full-box">
                            <FontAwesomeIcon icon={faSearch} className="dome-search-full-icon" />
                            <input
                                type="text"
                                placeholder="Search targets by representative name, designation, period, notes..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setCurrentPage(1);
                                }}
                                className="dome-search-full-input"
                            />
                            {search && (
                                <button
                                    type="button"
                                    className="dome-search-clear-mini"
                                    onClick={() => { setSearch(''); setCurrentPage(1); }}
                                    title="Clear search"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Row 2: Stacked Filters (Label on top, input below) */}
                    <div className="dome-opp-filters-row">
                        {/* Target Type Filter */}
                        <div className="dome-stacked-filter-item">
                            <label className="dome-stacked-filter-label">Target Type</label>
                            <select
                                value={typeFilter}
                                onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
                                className="dome-stacked-select"
                            >
                                <option value="ALL">All Targets ({targets.length})</option>
                                <option value="Amount">Revenue Quotas ({targets.filter(t => t.TargetType === 'Amount').length})</option>
                                <option value="Lead Generation">Lead Generation ({targets.filter(t => t.TargetType === 'Lead Generation').length})</option>
                                <option value="Lead Approaching">Lead Outreach ({targets.filter(t => t.TargetType === 'Lead Approaching').length})</option>
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div className="dome-stacked-filter-item">
                            <label className="dome-stacked-filter-label">Status</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                className="dome-stacked-select"
                            >
                                <option value="ALL">All Statuses</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Achieved">Achieved</option>
                                <option value="Exceeded">Exceeded</option>
                                <option value="Needs Attention">Needs Attention</option>
                            </select>
                        </div>

                        {/* Period Filter */}
                        {uniquePeriods.length > 0 && (
                            <div className="dome-stacked-filter-item">
                                <label className="dome-stacked-filter-label">Period</label>
                                <select
                                    value={periodFilter}
                                    onChange={(e) => { setPeriodFilter(e.target.value); setCurrentPage(1); }}
                                    className="dome-stacked-select"
                                >
                                    <option value="ALL">All Periods</option>
                                    {uniquePeriods.map(p => (
                                        <option key={p} value={p}>{p}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Start Date */}
                        <div className="dome-stacked-filter-item dome-stacked-date-item">
                            <label className="dome-stacked-filter-label">Start Date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                                className="dome-stacked-date-input"
                            />
                        </div>

                        {/* End Date */}
                        <div className="dome-stacked-filter-item dome-stacked-date-item">
                            <label className="dome-stacked-filter-label">End Date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                                className="dome-stacked-date-input"
                            />
                        </div>

                        {/* View Switcher: Table vs Cards */}
                        <div className="dome-stacked-filter-item">
                            <label className="dome-stacked-filter-label">View</label>
                            <div className="dome-view-toggle-group">
                                <button
                                    type="button"
                                    className={`dome-view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                                    onClick={() => setViewMode('table')}
                                    title="Table View"
                                >
                                    <FontAwesomeIcon icon={faTable} />
                                    <span>Table</span>
                                </button>
                                <button
                                    type="button"
                                    className={`dome-view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
                                    onClick={() => setViewMode('cards')}
                                    title="Cards View"
                                >
                                    <FontAwesomeIcon icon={faThLarge} />
                                    <span>Cards</span>
                                </button>
                            </div>
                        </div>

                        {/* Reset Filters Button */}
                        {isFiltered && (
                            <div className="dome-stacked-filter-item dome-stacked-reset-item">
                                <label className="dome-stacked-filter-label">&nbsp;</label>
                                <button
                                    type="button"
                                    className="dome-clear-filters-mini"
                                    onClick={handleResetFilters}
                                    title="Reset all filters"
                                >
                                    <FontAwesomeIcon icon={faTimes} className="mr-4px" />
                                    Clear
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* ================= 4. MAIN CONTENT CARD ================= */}
                <div className="dome-dash-card">
                    <div className="dome-dash-card-header">
                        <h3 className="dome-dash-card-title">Targets & Quotas Ledger</h3>
                        <div className="dome-table-header-controls">
                            <div className="dome-mini-filter-item">
                                <span className="dome-filter-mini-label">Sort:</span>
                                <select
                                    value={sortField}
                                    onChange={(e) => setSortField(e.target.value)}
                                    className="dome-mini-select"
                                >
                                    <option value="TargetID">ID</option>
                                    <option value="EmployeeName">Team Member</option>
                                    <option value="target">Target Goal</option>
                                    <option value="achieved">Achieved</option>
                                    <option value="progress">Attainment</option>
                                </select>
                                <button
                                    type="button"
                                    className="dome-sort-dir-mini"
                                    onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
                                    title={`Sort Order: ${sortOrder === 'asc' ? 'Ascending' : 'Descending'}`}
                                >
                                    <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountDown : faSortAmountUp} />
                                </button>
                            </div>

                            <div className="dome-mini-filter-item">
                                <span className="dome-filter-mini-label">Show:</span>
                                <select
                                    value={entries}
                                    onChange={(e) => {
                                        setEntries(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="dome-mini-select"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="p-20 text-center text-muted font-13">
                            <FontAwesomeIcon icon={faSyncAlt} spin className="mr-6px text-primary" />
                            Loading sales targets and quota allocations...
                        </div>
                    ) : filteredTargets.length === 0 ? (
                        <div className="p-20 text-center">
                            <p style={{ fontSize: '12px' }}>
                                {isFiltered
                                    ? "No allocations match your filter criteria."
                                    : "No targets have been assigned for this organization yet."
                                }
                            </p>
                            {isFiltered ? (
                                <button
                                    type="button"
                                    className="btn-dome-secondary"
                                    onClick={handleResetFilters}
                                >
                                    Reset Filters
                                </button>
                            ) : isAdmin && (
                                <button
                                    type="button"
                                    className="btn-dome-new"
                                    onClick={() => openCreate('Amount')}
                                    disabled={!canWrite}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                    <span>Assign First Target</span>
                                </button>
                            )}
                        </div>
                    ) : viewMode === 'table' ? (
                        /* ================= TABLE VIEW ================= */
                        <div className="dome-dash-table-wrap">
                            <table className="dome-dash-table dome-targets-table">
                                <thead>
                                    <tr>
                                        <th className="th-id cursor-pointer" onClick={() => handleSort('TargetID')}>
                                            ID {sortField === 'TargetID' && (
                                                <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon-inline" />
                                            )}
                                        </th>
                                        <th onClick={() => handleSort('EmployeeName')} className="cursor-pointer">
                                            Team Member {sortField === 'EmployeeName' && (
                                                <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon-inline" />
                                            )}
                                        </th>
                                        <th>Target Type</th>
                                        <th onClick={() => handleSort('target')} className="cursor-pointer">
                                            Target Goal {sortField === 'target' && (
                                                <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon-inline" />
                                            )}
                                        </th>
                                        <th onClick={() => handleSort('achieved')} className="cursor-pointer">
                                            Achieved {sortField === 'achieved' && (
                                                <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon-inline" />
                                            )}
                                        </th>
                                        <th onClick={() => handleSort('progress')} className="cursor-pointer" style={{ minWidth: '150px' }}>
                                            Attainment {sortField === 'progress' && (
                                                <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortAmountUp : faSortAmountDown} className="sort-icon-inline" />
                                            )}
                                        </th>
                                        <th>Gap</th>
                                        <th>Period</th>
                                        <th>Status</th>
                                        {isAdmin && <th className="text-right" style={{ width: '80px' }}>Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedTargets.map((target) => {
                                        const isAmount = target.TargetType === 'Amount';
                                        const progress = Number(target.ProgressPercent) || 0;
                                        const colorCls = getProgressColorClass(progress);

                                        return (
                                            <tr key={target.TargetID}>
                                                {/* ID */}
                                                <td>
                                                    <span className="dome-id-pill">{target.TargetID}</span>
                                                </td>

                                                {/* Team Member */}
                                                <td>
                                                    <div className="dome-lead-name-col">
                                                        <span className="dome-lead-name-link">
                                                            {target.EmployeeName || 'Unassigned'}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Type */}
                                                <td>
                                                    {isAmount
                                                        ? 'Revenue Quota'
                                                        : target.TargetType === 'Lead Generation'
                                                            ? 'Lead Generation'
                                                            : 'Lead Outreach'}
                                                </td>

                                                {/* Target Goal */}
                                                <td>
                                                    {isAmount
                                                        ? `$${Number(target.TargetDisplay || target.TargetAmount || 0).toLocaleString("en-US")}`
                                                        : `${target.TargetDisplay || target.TargetCount || 0} Leads`}
                                                </td>

                                                {/* Achieved */}
                                                <td>
                                                    {isAmount
                                                        ? `$${Number(target.AchievedDisplay || target.AchievedAmount || 0).toLocaleString("en-US")}`
                                                        : `${target.AchievedDisplay || (target.TargetType === 'Lead Generation' ? target.AchievedLeadsGenerated : target.AchievedLeadsApproached) || 0} Leads`}
                                                </td>

                                                {/* Attainment (Progress bar + %) */}
                                                <td>
                                                    <div className="dome-target-progress-wrap">
                                                        <div className="dome-target-progress-bar">
                                                            <div
                                                                className={`dome-target-progress-fill ${colorCls}`}
                                                                style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                                                            />
                                                        </div>
                                                        <span className="dome-target-progress-pct">{progress}%</span>
                                                    </div>
                                                </td>

                                                {/* Gap */}
                                                <td>
                                                    {isAmount
                                                        ? (Number(target.RemainingGap) > 0 ? `$${Number(target.RemainingGap).toLocaleString('en-US')}` : '$0')
                                                        : (Number(target.RemainingGap) > 0 ? `${target.RemainingGap} leads` : '0')}
                                                </td>

                                                {/* Period */}
                                                <td>
                                                    <div>
                                                        <div>{target.Period || defaultDates.label}</div>
                                                        {/* {target.StartDate && (
                                                            <div className="text-muted">
                                                                {target.StartDate.slice(0, 10)} - {target.EndDate ? target.EndDate.slice(0, 10) : ''}
                                                            </div>
                                                        )} */}
                                                    </div>
                                                </td>

                                                {/* Status */}
                                                <td>
                                                    <span className={`badge-status ${target.Status === 'Achieved' || target.Status === 'Exceeded' ? 'badge-paid' : target.Status === 'Needs Attention' ? 'badge-rejected' : 'badge-pending'}`}>
                                                        {target.Status || 'In Progress'}
                                                    </span>
                                                </td>

                                                {/* Actions */}
                                                {isAdmin && (
                                                    <td>
                                                        <div className="dome-row-actions" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                type="button"
                                                                className="dome-icon-btn"
                                                                title="Edit Target Quota"
                                                                onClick={() => handleEdit(target)}
                                                                disabled={!canWrite}
                                                            >
                                                                <FontAwesomeIcon icon={faPen} />
                                                            </button>
                                                            {canDelete && (
                                                                <button
                                                                    type="button"
                                                                    className="dome-icon-btn dome-icon-btn-danger"
                                                                    title="Delete Target"
                                                                    onClick={() => handleDelete(target.TargetID)}
                                                                    disabled={!canWrite}
                                                                >
                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        /* ================= CARDS VIEW ================= */
                        <div className="dome-targets-grid">
                            {paginatedTargets.map((target) => {
                                const isAmount = target.TargetType === 'Amount';
                                const progress = Number(target.ProgressPercent) || 0;
                                const colorCls = getProgressColorClass(progress);

                                return (
                                    <div key={target.TargetID} className="dome-target-card">
                                        <div className="dome-target-card-header">
                                            <div className="dome-target-user-info">
                                                <div className="dome-target-avatar">
                                                    {target.EmployeeName ? target.EmployeeName.charAt(0).toUpperCase() : 'U'}
                                                </div>
                                                <div>
                                                    <h4 className="dome-target-user-name">{target.EmployeeName || 'Unassigned'}</h4>
                                                    <p className="dome-target-user-role">{target.EmployeeDesignation || 'Sales Representative'}</p>
                                                </div>
                                            </div>

                                            <span className="dome-target-type-badge">
                                                {isAmount ? 'Revenue Quota' : target.TargetType === 'Lead Generation' ? 'Lead Gen' : 'Outreach'}
                                            </span>
                                        </div>

                                        <div className="dome-target-metrics-box">
                                            <div className="dome-target-metrics-row">
                                                <div className="dome-target-stat-col">
                                                    <span className="dome-target-stat-lbl">Achieved</span>
                                                    <span className="dome-target-stat-val val-blue">
                                                        {isAmount
                                                            ? `$${Number(target.AchievedDisplay || 0).toLocaleString("en-US")}`
                                                            : `${target.AchievedDisplay || 0} Leads`}
                                                    </span>
                                                </div>
                                                <div className="dome-target-stat-col text-right">
                                                    <span className="dome-target-stat-lbl">Target Goal</span>
                                                    <span className="dome-target-stat-val">
                                                        {isAmount
                                                            ? `$${Number(target.TargetDisplay || 0).toLocaleString("en-US")}`
                                                            : `${target.TargetDisplay || 0} Leads`}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="dome-target-progress-wrap mt-4">
                                                <div className="dome-target-progress-bar">
                                                    <div
                                                        className={`dome-target-progress-fill ${colorCls}`}
                                                        style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                                                    />
                                                </div>
                                                <span className="dome-target-progress-pct">{progress}%</span>
                                            </div>

                                            <div className="dome-target-metrics-row text-muted font-11 mt-2">
                                                <span>Status: {target.Status || 'In Progress'}</span>
                                                <span>
                                                    Gap: {isAmount
                                                        ? (Number(target.RemainingGap) > 0 ? `$${Number(target.RemainingGap).toLocaleString('en-US')}` : '$0')
                                                        : `${target.RemainingGap || 0} leads`}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="dome-target-meta-details">
                                            <div className="dome-target-meta-item">
                                                <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                                                <span>
                                                    Period: <strong>{target.Period || defaultDates.label}</strong>
                                                    {target.StartDate && ` (${target.StartDate.slice(0, 10)} to ${target.EndDate ? target.EndDate.slice(0, 10) : ''})`}
                                                </span>
                                            </div>

                                            {isAmount && (
                                                <div className="dome-target-meta-item">
                                                    <FontAwesomeIcon icon={faTrophy} className="text-muted" />
                                                    <span>
                                                        Active Pipeline: <strong>${Number(target.TotalPipelineAmount || 0).toLocaleString("en-US")}</strong> ({target.TotalAssignedDeals || 0} deals)
                                                    </span>
                                                </div>
                                            )}

                                            {target.Notes && (
                                                <div className="text-muted font-11 italic mt-4">
                                                    "{target.Notes}"
                                                </div>
                                            )}
                                        </div>

                                        {isAdmin && (
                                            <div className="dome-target-card-footer">
                                                <span className="font-11 text-muted">ID: {target.TargetID}</span>
                                                <div className="dome-row-actions">
                                                    <button
                                                        type="button"
                                                        className="dome-icon-btn"
                                                        title="Edit Target Quota"
                                                        onClick={() => handleEdit(target)}
                                                        disabled={!canWrite}
                                                    >
                                                        <FontAwesomeIcon icon={faPen} />
                                                    </button>
                                                    {canDelete && (
                                                        <button
                                                            type="button"
                                                            className="dome-icon-btn dome-icon-btn-danger"
                                                            title="Delete Target"
                                                            onClick={() => handleDelete(target.TargetID)}
                                                            disabled={!canWrite}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* ================= 5. TABLE FOOTER & PAGINATION ================= */}
                    <div className="dome-table-footer">
                        <span className="dome-table-counter">
                            Showing {sortedTargets.length === 0 ? 0 : (currentPage - 1) * entries + 1}&ndash;{Math.min(currentPage * entries, sortedTargets.length)} of {sortedTargets.length} entries
                            {isFiltered && ` (filtered from ${targets.length} total)`}
                        </span>

                        <div className="dome-pagination-controls">
                            <button
                                type="button"
                                className="dome-page-btn"
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                            >
                                Prev
                            </button>

                            <span className="dome-page-indicator">
                                Page {currentPage} of {totalPages || 1}
                            </span>

                            <button
                                type="button"
                                className="dome-page-btn"
                                disabled={currentPage === totalPages || totalPages === 0}
                                onClick={() => setCurrentPage(p => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>

                </div>

                {/* ================= ASSIGN / EDIT TARGET MODAL ================= */}
                {showModal && (
                    <div className="modal-overlay" onClick={() => setShowModal(false)}>
                        <div className="modal dome-modal-card" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header modal-header-flex">
                                <h3>{editMode ? `Edit Sales Target (${form.TargetID})` : "Assign New Sales Target"}</h3>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="modal-btn-close"
                                    title="Close Modal"
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </button>
                            </div>

                            <form onSubmit={handleSave} className="d-flex flex-col flex-1 overflow-hidden">
                                <div className="modal-body">
                                    <div className="form-grid">

                                        {/* Team Member Search & Select */}
                                        <div className="input-group input-group-span-2">
                                            <label>Assign To Team Member <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                placeholder="Filter team member list by name, email, or designation..."
                                                value={modalUserSearch}
                                                onChange={(e) => setModalUserSearch(e.target.value)}
                                                className="mb-6"
                                            />
                                            <select
                                                value={form.AssignedTo}
                                                onChange={(e) => setForm({ ...form, AssignedTo: e.target.value })}
                                                required
                                            >
                                                <option value="">-- Select Team Member ({filteredAssignableUsers.length} available) --</option>
                                                {filteredAssignableUsers.map(u => (
                                                    <option key={u.empId} value={u.empId}>
                                                        {u.name} ({u.designation || 'Representative'}) — {u.email}
                                                    </option>
                                                ))}
                                            </select>
                                            {filteredAssignableUsers.length === 0 && (
                                                <small className="text-danger mt-4 d-block font-11">
                                                    No team members match "{modalUserSearch}".
                                                </small>
                                            )}
                                        </div>

                                        {/* Target Type Selector */}
                                        <div className="input-group input-group-span-2">
                                            <label>Target Type <span className="text-danger">*</span></label>
                                            <select
                                                value={form.TargetType}
                                                onChange={(e) => {
                                                    const newType = e.target.value;
                                                    setForm(prev => ({
                                                        ...prev,
                                                        TargetType: newType,
                                                        TargetAmount: newType === 'Amount' ? (prev.TargetAmount || '2500000') : prev.TargetAmount,
                                                        TargetCount: newType !== 'Amount' ? (prev.TargetCount || 25) : prev.TargetCount
                                                    }));
                                                }}
                                            >
                                                <option value="Amount">Revenue Quota (Target in USD $)</option>
                                                <option value="Lead Generation">Lead Generation (New Accounts / Leads)</option>
                                                <option value="Lead Approaching">Lead Outreach (Accounts Approached)</option>
                                            </select>
                                        </div>

                                        {/* Conditional Field: Amount vs Count */}
                                        {form.TargetType === 'Amount' ? (
                                            <div className="input-group">
                                                <label>Target Amount (USD $) <span className="text-danger">*</span></label>
                                                <input
                                                    type="number"
                                                    value={form.TargetAmount}
                                                    onChange={(e) => setForm({ ...form, TargetAmount: e.target.value })}
                                                    placeholder="e.g. 2500000"
                                                    required
                                                />
                                            </div>
                                        ) : (
                                            <div className="input-group">
                                                <label>{form.TargetType} Target Goal (Count) <span className="text-danger">*</span></label>
                                                <input
                                                    type="number"
                                                    value={form.TargetCount}
                                                    onChange={(e) => setForm({ ...form, TargetCount: e.target.value })}
                                                    placeholder="e.g. 25"
                                                    required
                                                />
                                            </div>
                                        )}

                                        {/* Period Name */}
                                        <div className="input-group">
                                            <label>Period Name</label>
                                            <input
                                                type="text"
                                                value={form.Period}
                                                onChange={(e) => setForm({ ...form, Period: e.target.value })}
                                                placeholder="e.g. H1 FY26"
                                            />
                                        </div>

                                        {/* Status */}
                                        <div className="input-group">
                                            <label>Target Status</label>
                                            <select
                                                value={form.Status}
                                                onChange={(e) => setForm({ ...form, Status: e.target.value })}
                                            >
                                                <option value="In Progress">In Progress</option>
                                                <option value="Achieved">Achieved</option>
                                                <option value="Exceeded">Exceeded</option>
                                                <option value="Needs Attention">Needs Attention</option>
                                            </select>
                                        </div>

                                        {/* Start Date */}
                                        <div className="input-group">
                                            <label>Start Date</label>
                                            <input
                                                type="date"
                                                value={form.StartDate}
                                                onChange={(e) => setForm({ ...form, StartDate: e.target.value })}
                                            />
                                        </div>

                                        {/* End Date */}
                                        <div className="input-group">
                                            <label>End Date</label>
                                            <input
                                                type="date"
                                                value={form.EndDate}
                                                onChange={(e) => setForm({ ...form, EndDate: e.target.value })}
                                            />
                                        </div>

                                        {/* Notes */}
                                        <div className="input-group input-group-span-2">
                                            <label>Strategic Guidance & Performance Notes</label>
                                            <textarea
                                                value={form.Notes}
                                                onChange={(e) => setForm({ ...form, Notes: e.target.value })}
                                                placeholder="Key priorities, enterprise healthcare targets, quota guidance..."
                                                rows={2}
                                            />
                                        </div>

                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn-dome-secondary"
                                        onClick={() => setShowModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn-dome-new"
                                    >
                                        {editMode ? "Save Changes" : "Assign Target"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* ================= CONFIRMATION MODAL ================= */}
                {confirmModal.open && (
                    <div className="modal-overlay">
                        <div className="modal modal-confirm-sm">
                            <div className="modal-header">
                                <h3>{confirmModal.title}</h3>
                            </div>
                            <div className="modal-body">
                                <p className="confirm-modal-text">
                                    {confirmModal.message}
                                </p>
                                <div className="modal-footer mt-20">
                                    <button
                                        type="button"
                                        className="btn-dome-secondary"
                                        onClick={() => setConfirmModal({ open: false, title: '', message: '', onConfirm: null })}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-dome-new"
                                        onClick={() => {
                                            if (typeof confirmModal.onConfirm === 'function') {
                                                confirmModal.onConfirm();
                                            }
                                            setConfirmModal({ open: false, title: '', message: '', onConfirm: null });
                                        }}
                                    >
                                        Confirm
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </Layout>
    );
}

export default Targets;
